import React, { useState } from "react";
import { Box, Button, Grid, LinearProgress, Modal, 
  TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import AnimateHeight from "react-animate-height";
import { useSnack } from "../../../../utils/useSnack";
import { moderatorService } from "../../../../api/services/moderator";
import { ReviewResult, StorageType } from "../../../../utils/enums";
import useSignature from "../../../../api/services/signature";
import { b64EncodeUnicode } from "../../../../utils/base64";
import { useDictionary } from "../../../../utils/useDictionary";
import { useSelector } from "react-redux";
import { State } from "../../../../redux/store";
import SignatureBtn from "../../../../common/SignatureBtn";
import { modalStyle } from "../../../../common/modalStyle";

export const DeclineModal = ({
  isOpen, onClose, auctionId, onAuctionClose
}: {
  isOpen: boolean;
  onClose: () => void;
  auctionId: number;
  onAuctionClose: () => void;
}) => {
  const { t } = useTranslation();
  const { snack } = useSnack();
  const state = useSelector((state: State) => state);

  {/* SIGNING */}
    const [signature, setSignature] = useState("");
    const storageType = useDictionary(
      StorageType, 
      (type) => ({value: type, label: t(`storage_type.${type}`)}),
      { value: StorageType.PKCS12, label: t("storage_type.PKCS12") }
    );
    const { signXML } = useSignature();
    const [isSigning, setIsSigning] = useState(false);
    const [signingError, setSigningError] = useState("");
    async function onSign() {
      if (!declineReason) {
        snack(t("snack.warning.decline_reason"), "warning");
        return
      }

      try {
        if (!state.auth.user || !storageType.selected) return;
        setIsSigning(true);

        const declineDetails = {
          documentType: t("auction.modals.decline.decline_by_moderator"),
          auctionId: auctionId,
          author: state.auth.user.name,
        }
        const declineDetailsXml = `<decline>${b64EncodeUnicode(JSON.stringify(declineDetails))}</decline>`
        const signedXml = await signXML('SIGN', declineDetailsXml);
        setSignature(signedXml.result);
        snack(t("snack.success.signed_confirm"), "success");
      }
      catch (error) {
        console.error("ERROR", error);

        if (error === "NO_NCALAYER") {
          snack(t("snack.error.no_ncalayer"), "error")
        } else if (error === "storage.empty") { 
          snack(t("snack.error.storage_empty"), "error") 
        }
      }
      finally { setIsSigning(false) }
    };
  {/* / SIGNING */}

  const [declineReason, setDeclineReason] = React.useState("");
  
  const handleTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>, 
    setState: React.Dispatch<React.SetStateAction<string>>
  ) => { setState(e.target.value) };

  const [isLoading, setIsLoading] = React.useState(false);
  const declineAuction = async () => {
    if (declineReason) {
      const data = { 
        result: ReviewResult.declined,
        declineReason, 
        signature: b64EncodeUnicode(signature)
      };

      try {
        setIsLoading(true);
        const result = await moderatorService.reviewAuction(auctionId, data);
        if (result.status === "success") {
          snack(t("snack.success.auction_declined"), "success"); 
        }
        else { snack(t("snack.error.auction_decline"), "error")}
        onAuctionClose();
      }
      catch (error) {
        const err = error as AxiosError;
        snack(t("snack.error.auction_decline"), "error"); 
        onCancel()
      }
      finally { setIsLoading(false) }
    }
    else { snack(t("snack.warning.decline_reason"), "warning") }
  };

  const onCancel = () => { setDeclineReason(""); onClose() };
  
  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={modalStyle}>
        {/* header */}
          <Grid container spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
            <Grid item>
              <Typography component="h5" variant="h5" sx={{ fontSize: { xs: 14, sm: 20 } }}>
                {t("auction.modals.decline.reason")}
              </Typography>
            </Grid>
            <Grid item xs={2.2} sm={1.2}>
              <Button onClick={onClose} sx={{ 
                color: "black"
              }}>
                <CloseIcon />
              </Button>
            </Grid>
          </Grid>
        {/* / header */}

        <AnimateHeight height={isLoading ? 50 : "auto"} duration={200}>
          {isLoading 
            ? <Box>
                <LinearProgress sx={{ mt: 3, mb: 1 }} color="primary" />
                <Typography>{t("auction.modals.decline.declining")}</Typography>
              </Box>
            : <Grid container spacing={2} direction={"column"}>
                <Grid item>
                  <TextField
                    fullWidth color="primary" type="text" size="small"
                    name="declineReason" label={t("auction.fields.decline_reason")}
                    value={declineReason}
                    onChange={e => handleTextChange(e, setDeclineReason)}
                  />
                </Grid>

                <SignatureBtn 
                  onSign={onSign} 
                  isSigning={isSigning} 
                  signingError={signingError} 
                  storageType={storageType} 
                  signed={signature ? true : false}
                />
              </Grid>
          }
        </AnimateHeight>

        <Grid item display={"flex"} justifyContent={"flex-end"} mt={2}>
          <Button variant="contained" size="small" sx={{ mr: 1 }}
                  onClick={onCancel} disabled={isLoading} color="secondary"
          >
            {t("auction.modals.common.cancel")}
          </Button>
          <Button variant="contained" color="primary" size="small"
                  onClick={declineAuction} disabled={!signature || isLoading}
          >
            {t("auction.modals.decline.confirm_decline")}
          </Button>
        </Grid>
      </Box>
    </Modal>
  )
}
